/**
 * Shared shapes for the three "dailies" panels (Health, Todos, Media).
 *
 * NO React imports anywhere under src/lib/dailies — this whole folder is pure,
 * synchronously testable logic. Midnight rollover and release-date transitions
 * are graded acceptance criteria, and the only way to reason about them with
 * confidence is to keep them out of component bodies.
 */

/* ------------------------------ health ------------------------------ */

/** A user-defined checklist row. Definitions persist forever (never reset). */
export interface HealthField {
  readonly id: string;
  readonly label: string;
}

/* ------------------------------- todos ------------------------------ */

/** One aspiration/todo for today. The whole list resets at local midnight. */
export interface Todo {
  readonly id: string;
  readonly text: string;
  readonly done: boolean;
}

/* ------------------------------- media ------------------------------ */

export const MEDIA_TYPES = ['game', 'anime', 'movie', 'other'] as const;

export type MediaType = (typeof MEDIA_TYPES)[number];

export interface MediaItem {
  readonly id: string;
  readonly title: string;
  /**
   * EXACTLY what `<input type="date">` produced: a bare 'YYYY-MM-DD' local
   * calendar date key, or '' when the user left it blank.
   *
   * Never a Date, never an ISO timestamp, never passed to `new Date()`.
   * `new Date('2026-09-23')` is parsed as UTC midnight, which is Sep 22 local
   * in every negative-offset timezone — every tracked item would flip a day
   * early. Compare the strings; see src/lib/dailies/media.ts.
   */
  readonly releaseDate: string;
  readonly type: MediaType;
}

/** The four states a tracked item's release date can be in, relative to today. */
export type ReleaseStatus = 'released-today' | 'past' | 'upcoming' | 'invalid';

/* ------------------------------ helpers ----------------------------- */

/** Rendered in the date column when a release date is missing or malformed. */
export const EM_DASH = '—';

/** True for any array. The coarse gate before a migrate* pass salvages rows. */
export function isUnknownArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}

/** Narrows an unknown blob row to something with indexable properties. */
export function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Trimmed string, or '' for anything that is not a string. Never throws. */
export function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
